import { useEffect, useRef, useState, useCallback } from "react";
import "./FloorPlanCard.css";

function FloorPlanCard({
  title = "Unit Layouts",
  description = "Thoughtfully Designed Floor Plans for Every Home",
  layouts = [],
  accentColor = "",
  titleColor = "",
  descColor = "",
  cardTitleColor = "",
  cardDescColor = "",
  cardColor = "",
  dotNavWrapperColor = "",
  dotNavColor = "",
  dotNavProgressColor = "",
  dotNavHoverColor = "",
  mediaBtnBgColor = "",
  mediaBtnSvgColor = "",
  mediaBtnHoverColor = "",
}) {
  const DURATION = 5000;
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [transition, setTransition] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  const galleryRef = useRef(null);
  const titleRef = useRef(null);
  const containerRef = useRef(null);
  const wasPlayingRef = useRef(false);
  const userPausedRef = useRef(false);
  const elapsedRef = useRef(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const cardRefs = useRef([]);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const deltaX = useRef(0);
  const isDragging = useRef(false);
  const isHorizontal = useRef(null);
  const currentTranslate = useRef(0);

  // Scroll trigger: autoplay when 70% of gallery visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.intersectionRatio >= 0.7;
        if (visible) {
          // Only autoplay if user did NOT pause when its visible
          if (!hasEnded && !userPausedRef.current) {
            setPlaying(true);
          }
        } else {
          // Leaving viewport → pause if playing
          if (playing) {
            wasPlayingRef.current = true;
            setPlaying(false);
          }
        }
      },
      { threshold: [0, 0.7] },
    );

    if (galleryRef.current) observer.observe(galleryRef.current);

    return () => observer.disconnect();
  }, [playing, hasEnded]);

  // Scroll trigger: title animation
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("in-view");
          observer.unobserve(el); // animate once
        }
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Playing progress logic
  useEffect(() => {
    if (!playing) return;

    const tick = (time) => {
      if (!startTimeRef.current) {
        startTimeRef.current = time - elapsedRef.current;
      }

      const elapsed = time - startTimeRef.current;
      const nextProgress = Math.min(elapsed / DURATION, 1);

      setProgress(nextProgress);

      if (nextProgress === 1) {
        elapsedRef.current = 0;
        startTimeRef.current = null;

        setCurrent((prev) => {
          if (prev === layouts.length - 1) {
            setPlaying(false);
            setHasEnded(true);
            return prev;
          }
          return prev + 1;
        });

        setProgress(0);
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, current, layouts]);

  //Pause without resetting time
  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(rafRef.current);
      elapsedRef.current = progress * 5000;
      startTimeRef.current = null;
    }
  }, [playing, progress]);

  const goToDotNav = (targetIndex, options = {}) => {
    const {
      source = "click", // "click" | "drag" | "autoplay"
    } = options;

    if (targetIndex === current) return;

    cancelAnimationFrame(rafRef.current);
    elapsedRef.current = 0;
    startTimeRef.current = null;
    setProgress(0);
    setPlaying(false);
    userPausedRef.current = true;

    const direction = targetIndex > current ? 1 : -1;
    const distance = Math.abs(targetIndex - current);
    const duration = getDuration(distance, source);

    // 🔑 dynamically control dotnav speed
    document.documentElement.style.setProperty(
      "--dotnav-duration",
      `${duration}ms`,
    );

    document.documentElement.style.setProperty(
      "--card-duration",
      `${duration * 1.1}ms`,
    );

    let stepIndex = current;

    const step = () => {
      const from = stepIndex;
      const to = from + direction;

      // 🔑 move ownership FIRST
      setCurrent(to);

      // 🔑 animate transfer
      setTransition({ from, to });

      stepIndex = to;

      setTimeout(() => {
        setTransition(null);

        if (stepIndex !== targetIndex) {
          step();
        }
      }, duration);
    };

    step();
  };

  const getDuration = (distance, source) => {
    let base; //click

    if (distance <= 1) base = 1000;
    else if (distance === 2) base = 500;
    else if (distance === 3) base = 250;
    else base = 150;

    if (source === "drag") {
      return Math.max(220, base * 0.35);
    }

    if (source === "autoplay") {
      return base;
    }

    return base;
  };

  const handlePlayPauseReplay = () => {
    if (playing) {
      userPausedRef.current = true;
      setPlaying(false);
    } else if (hasEnded) {
      userPausedRef.current = false;
      setCurrent(0);
      setHasEnded(false);
      setPlaying(true);
    } else {
      userPausedRef.current = false;
      setPlaying(true);
    }
  };

  const PlayIcon = () => (
    <svg viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  const PauseIcon = () => (
    <svg viewBox="0 0 24 24">
      <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
    </svg>
  );

  const ReplayIcon = () => (
    <svg viewBox="0 0 24 24">
      <path d="M12 5v4l5-5-5-5v4C6.5 3 3 7.5 3 12s3.5 9 9 9 9-4 9-9h-2c0 3.9-3.1 7-7 7s-7-3.1-7-7 3.1-7 7-7z" />
    </svg>
  );

  // Centering active card
  const centerActiveCard = useCallback(() => {
    const container = containerRef.current;
    const activeCard = cardRefs.current[current];
    if (!container || !activeCard) return;

    const containerRect = container.getBoundingClientRect();
    const cardRect = activeCard.getBoundingClientRect();

    const containerCenter = containerRect.width / 2;
    const cardCenter = activeCard.offsetLeft + cardRect.width / 2;

    const translateX = containerCenter - cardCenter;

    currentTranslate.current = translateX;
    container.style.transform = `translateX(${translateX}px)`;
  }, [current]);

  useEffect(() => {
    centerActiveCard();
  }, [centerActiveCard]);

  useEffect(() => {
    let rafId;

    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(centerActiveCard);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [centerActiveCard]);

  const isTouchDevice = () => window.matchMedia("(pointer: coarse)").matches;

  const onTouchStart = (e) => {
    if (!isTouchDevice()) return;
    if (viewerImage) return;

    const touch = e.touches[0];

    startX.current = touch.clientX;
    startY.current = touch.clientY;
    startTime.current = performance.now();
    deltaX.current = 0;

    isHorizontal.current = null;
    isDragging.current = false;

    containerRef.current.style.transition = "none";
  };

  const onTouchMove = (e) => {
    if (viewerImage) return;
    if (!e.touches.length) return;

    const touch = e.touches[0];
    const dx = touch.clientX - startX.current;
    const dy = touch.clientY - startY.current;

    if (isHorizontal.current === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      isHorizontal.current = Math.abs(dx) > Math.abs(dy);
    }

    if (!isHorizontal.current) {
      isDragging.current = false;
      return;
    }

    isDragging.current = true;

    deltaX.current = dx;
    const activeCard = cardRefs.current[current];
    const PAUSE_THRESHOLD = activeCard.offsetWidth * 0.15;

    if (Math.abs(deltaX.current) > PAUSE_THRESHOLD) {
      userPausedRef.current = true;
      setPlaying(false);
      setProgress(0);
    }

    // 🚫 boundary lock
    if (
      (current === 0 && deltaX.current > 0) ||
      (current === layouts.length - 1 && deltaX.current < 0)
    ) {
      deltaX.current *= 0.6; // resistance
    }

    containerRef.current.style.transform = `translateX(${currentTranslate.current + deltaX.current}px)`;
  };

  const onTouchEnd = () => {
    if (viewerImage) return;
    if (!isDragging.current) return;
    isDragging.current = false;

    restoreTransition();

    const targetIndex = computeTargetIndexFromDrag();
    const clampedIndex = Math.max(0, Math.min(layouts.length - 1, targetIndex));

    if (clampedIndex !== current) {
      goToDotNav(clampedIndex, { source: "drag" }); // 🔑 reuse your proven logic
    } else {
      // snap back
      containerRef.current.style.transform = `translateX(${currentTranslate.current}px)`;
    }
  };

  const restoreTransition = () => {
    const el = containerRef.current;
    if (!el) return;

    // force reflow so Safari commits "transition: none"
    el.getBoundingClientRect();

    // restore CSS-driven transition
    el.style.transition = "";
  };

  const computeTargetIndexFromDrag = () => {
    const activeCard = cardRefs.current[current];
    const cardWidth = activeCard.offsetWidth;

    const elapsed = performance.now() - startTime.current;
    const velocityPxPerMs = deltaX.current / elapsed;

    const INERTIA_TIME = 200;
    const projected = deltaX.current + velocityPxPerMs * INERTIA_TIME;

    const projectedCards = projected / cardWidth;

    // round toward direction of travel
    let steps = Math.round(projectedCards);

    // safety
    steps = Math.max(-2, Math.min(2, steps)); // max skip 2

    return current - steps;
  };

  const highlightNumbers = (text) =>
    text.split(/(\d+)/).map((part, i) =>
      /\d+/.test(part) ? (
        <span key={i} className="highlight-num" style={{ color: accentColor }}>
          {part}
        </span>
      ) : (
        part
      ),
    );

  useEffect(() => {
    if (viewerImage) {
      document.body.style.overflow = "hidden";
      userPausedRef.current = true;
      setPlaying(false);
      setProgress(0);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewerImage]);

  // Page visibility (tabs, mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden → pause
        wasPlayingRef.current = playing;
        setPlaying(false);
      } else {
        // Tab is visible again
        if (wasPlayingRef.current && !hasEnded && !userPausedRef.current) {
          setPlaying(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [playing, hasEnded]);

  // Window focus (desktop apps)
  useEffect(() => {
    const handleBlur = () => {
      wasPlayingRef.current = playing;
      setPlaying(false);
    };

    const handleFocus = () => {
      if (wasPlayingRef.current && !hasEnded && !userPausedRef.current) {
        setPlaying(true);
      }
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [playing, hasEnded]);

  return (
    <section
      id="floor-plan-section"
      className="highlights-gallery"
      ref={galleryRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        "--title-color": titleColor,
        "--desc-color": descColor,
      }}
    >
      <div className="title" ref={titleRef}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div className="cards-container" ref={containerRef}>
        {layouts.map((layout, index) => (
          <div
            key={layout.id}
            ref={(el) => (cardRefs.current[index] = el)}
            className={`card ${index === current ? "active" : ""}`}
            style={{ 
              background: cardColor,
              "--card-title-color": cardTitleColor,
              "--card-desc-color": cardDescColor,
            }}
          >
            <div className="media">
              <img
                src={layout.image}
                alt={layout.unitType}
                className={index === current ? "clickable" : ""}
                onClick={() => {
                  if (index === current) {
                    setViewerImage(layout.image);
                  }
                }}
              />
            </div>
            <div className="caption">
              <h1>{layout.unitType}</h1>
              <p>{highlightNumbers(layout.description)}</p>
            </div>
          </div>
        ))}
      </div>

      {viewerImage && (
        <div className="image-viewer" onClick={() => setViewerImage(null)}>
          <img
            src={viewerImage}
            alt="Full view"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div
        className="controls"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        style={{
          "--dotnav-wrapper-color": dotNavWrapperColor,
          "--dotnav-color": dotNavColor,
          "--dotnav-progress-color": dotNavProgressColor,
          "--dotnav-hover-color": dotNavHoverColor,
          "--mediabtn-bg-color": mediaBtnBgColor,
          "--mediabtn-svg-color": mediaBtnSvgColor,
          "--mediabtn-hover-color": mediaBtnHoverColor,
        }}
      >
        <div className="controls-inner">
          <div className="dotnav-wrapper">
            {layouts.map((_, i) => (
              <button
                key={i}
                className={`dotnav 
                          ${i === current ? "active" : ""} 
                          ${i === transition?.from ? "collapsing" : ""}
                          ${i === transition?.to ? "expanding" : ""} 
                          ${playing ? "playing" : "paused"}`}
                style={i === current ? { "--duration": "5000ms" } : {}}
                onClick={() => goToDotNav(i, { source: "click" })}
              >
                <span
                  className="dotnav-progress"
                  style={
                    i === current
                      ? { "--progress": progress }
                      : { "--progress": 0 }
                  }
                />
              </button>
            ))}
          </div>

          <div className="media-controls">
            <button
              className="media-btn"
              onClick={handlePlayPauseReplay}
              aria-label={playing ? "Pause" : hasEnded ? "Replay" : "Play"}
            >
              {hasEnded ? (
                <ReplayIcon />
              ) : playing ? (
                <PauseIcon />
              ) : (
                <PlayIcon />
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FloorPlanCard;
